// src/components/ErrorBoundary.js
/**
 * Error Boundary Component - Comprehensive error handling
 * 
 * Provides:
 * - React error boundary for catching rendering errors
 * - Fallback UI with error details
 * - Error logging to Firebase
 * - Recovery actions
 * - User-friendly error messages
 * - Integration with telemetry service
 */

import React, { Component } from 'react';
import { logOdin } from '../services/telemetry';
import { isChunkLoadError, reloadOnceOnChunkError } from '../utils/lazyWithChunkReload';
import './ErrorBoundary.css';

// ═══════════════════════════════════════════════════════════════════════════════
// ERROR BOUNDARY COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            chunkReloading: false,
            error: null,
            errorInfo: null,
            errorCount: 0,
            lastErrorTime: null
        };
    }

    static getDerivedStateFromError(error) {
        if (isChunkLoadError(error)) {
            return { hasError: false, chunkReloading: true };
        }
        // Update state so the next render will show the fallback UI
        return { hasError: true, chunkReloading: false };
    }

    componentDidCatch(error, errorInfo) {
        // Post-deploy stale chunk: one hard reload usually recovers.
        if (isChunkLoadError(error)) {
            if (reloadOnceOnChunkError()) {
                return;
            }
            // Cooldown already used — fall through to normal error UI
            this.setState({ hasError: true, chunkReloading: false });
        }

        const { errorCount, lastErrorTime } = this.state;
        const now = Date.now();

        // Check if this is a repeated error
        const isRepeatedError = lastErrorTime && (now - lastErrorTime) < 5000;

        // Log error to console
        console.error('ErrorBoundary caught an error:', error, errorInfo);

        // Log to telemetry service
        this.logError(error, errorInfo);

        // Update state with error details
        this.setState({
            error,
            errorInfo,
            errorCount: isRepeatedError ? errorCount + 1 : 1,
            lastErrorTime: now
        });

        // Call custom error handler if provided
        if (this.props.onError) {
            this.props.onError(error, errorInfo);
        }
    }

    /**
     * Log error to telemetry service
     */
    async logError(error, errorInfo) {
        try {
            await logOdin('Error Boundary Caught Error', 'error', {
                errorMessage: error?.toString() || 'Unknown error',
                errorStack: error?.stack || 'No stack trace',
                componentStack: errorInfo?.componentStack || 'No component stack',
                userAgent: navigator.userAgent,
                url: window.location.href,
                timestamp: new Date().toISOString(),
                context: this.props.context || 'unknown'
            });
        } catch (logError) {
            console.error('Failed to log error:', logError);
        }
    }

    /**
     * Handle error recovery
     */
    handleReset = () => {
        this.setState({
            hasError: false,
            chunkReloading: false,
            error: null,
            errorInfo: null
        });

        // Call custom reset handler if provided
        if (this.props.onReset) {
            this.props.onReset();
        }

        // Reload if specified
        if (this.props.reloadOnReset) {
            window.location.reload();
        }
    };

    /**
     * Navigate to home
     */
    handleGoHome = () => {
        window.location.href = '/';
    };

    /**
     * Copy error details to clipboard
     */
    handleCopyError = async () => {
        const { error, errorInfo } = this.state;
        const errorText = `
Error: ${error?.toString() || 'Unknown error'}

Stack Trace:
${error?.stack || 'No stack trace'}

Component Stack:
${errorInfo?.componentStack || 'No component stack'}

User Agent: ${navigator.userAgent}
URL: ${window.location.href}
Time: ${new Date().toISOString()}
        `.trim();

        try {
            await navigator.clipboard.writeText(errorText);
            alert('Error details copied to clipboard');
        } catch (err) {
            console.error('Failed to copy error details:', err);
        }
    };

    render() {
        const { hasError, chunkReloading, error, errorInfo, errorCount } = this.state;
        const {
            children,
            fallback,
            showDetails = process.env.NODE_ENV === 'development',
            showReset = true,
            customMessage,
            context = 'Application'
        } = this.props;

        if (chunkReloading && !hasError) {
            return children;
        }

        if (hasError) {
            // Use custom fallback if provided
            if (fallback) {
                return typeof fallback === 'function' 
                    ? fallback({ error, errorInfo, onReset: this.handleReset })
                    : fallback;
            }

            // Default error UI
            return (
                <div className="error-boundary">
                    <div className="error-boundary-content">
                        <div className="error-boundary-icon">⚠️</div>
                        
                        <h1 className="error-boundary-title">
                            Something went wrong
                        </h1>
                        
                        <p className="error-boundary-message">
                            {customMessage || `An unexpected error occurred in ${context}. We've logged this issue and will look into it.`}
                        </p>

                        {errorCount > 3 && (
                            <div className="error-boundary-alert">
                                <strong>Multiple errors detected.</strong> This may indicate a persistent issue. 
                                Please try refreshing the page or contact support.
                            </div>
                        )}

                        <div className="error-boundary-actions">
                            {showReset && (
                                <button 
                                    className="error-boundary-btn error-boundary-btn--primary" 
                                    onClick={this.handleReset}
                                >
                                    Try Again
                                </button>
                            )}
                            
                            <button 
                                className="error-boundary-btn" 
                                onClick={this.handleGoHome}
                            >
                                Go to Home
                            </button>

                            <button 
                                className="error-boundary-btn error-boundary-btn--secondary" 
                                onClick={() => window.location.reload()}
                            >
                                Refresh Page
                            </button>
                        </div>

                        {showDetails && error && (
                            <details className="error-boundary-details">
                                <summary>Error Details (for developers)</summary>
                                
                                <div className="error-boundary-details-content">
                                    <div className="error-detail-section">
                                        <h3>Error Message:</h3>
                                        <pre>{error.toString()}</pre>
                                    </div>

                                    {error.stack && (
                                        <div className="error-detail-section">
                                            <h3>Stack Trace:</h3>
                                            <pre>{error.stack}</pre>
                                        </div>
                                    )}

                                    {errorInfo?.componentStack && (
                                        <div className="error-detail-section">
                                            <h3>Component Stack:</h3>
                                            <pre>{errorInfo.componentStack}</pre>
                                        </div>
                                    )}

                                    <button 
                                        className="error-boundary-btn error-boundary-btn--small"
                                        onClick={this.handleCopyError}
                                    >
                                        Copy Error Details
                                    </button>
                                </div>
                            </details>
                        )}

                        <p className="error-boundary-footer">
                            If this problem persists, please contact support with the error details.
                        </p>
                    </div>
                </div>
            );
        }

        return children;
    }
}

// ═══════════════════════════════════════════════════════════════════════════════
// ERROR BOUNDARY HOC
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Higher-order component to wrap a component with error boundary
 */
export function withErrorBoundary(Component, errorBoundaryProps = {}) {
    const WrappedComponent = (props) => (
        <ErrorBoundary {...errorBoundaryProps}>
            <Component {...props} />
        </ErrorBoundary>
    );

    WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name || 'Component'})`;

    return WrappedComponent;
}

// ═══════════════════════════════════════════════════════════════════════════════
// EXPORTS
// ═══════════════════════════════════════════════════════════════════════════════

export default ErrorBoundary;

